/* © NXAPP Limited */
import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { ArrowLeft, MousePointer, Eye, AlertTriangle, UserX, Send, X } from 'lucide-react'
import Spinner from '@/components/ui/Spinner'

const rate = (n: number, total: number) => (total > 0 ? Math.round((n / total) * 100) : 0)

function Stat({ label, value, sub, icon: Icon, onClick, accent }: { label: string; value: number; sub: string; icon: typeof Eye; onClick?: () => void; accent?: string }) {
  const inner = (
    <>
      <div className="flex items-center gap-2 text-gray-500 text-sm mb-2">
        <Icon className="w-4 h-4" /> {label}
      </div>
      <p className={`text-3xl font-bold ${accent ?? 'text-gray-900'}`}>{value.toLocaleString()}</p>
      <p className="text-xs text-gray-400 mt-1">{sub}</p>
    </>
  )
  if (onClick) return (
    <button onClick={onClick} className="card p-5 text-left hover:border-brand-300 hover:shadow-sm transition-all">
      {inner}
      <p className="text-[11px] text-brand-600 mt-2">View who →</p>
    </button>
  )
  return <div className="card p-5">{inner}</div>
}

function Bar({ label, n, total, colour }: { label: string; n: number; total: number; colour: string }) {
  const r = rate(n, total)
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-gray-600">{label}</span>
        <span className="text-gray-500">{n.toLocaleString()} · <span className="font-medium text-gray-700">{r}%</span></span>
      </div>
      <div className="h-2.5 rounded-full bg-gray-100 overflow-hidden">
        <div className={`h-full rounded-full ${colour}`} style={{ width: `${r}%` }} />
      </div>
    </div>
  )
}

export default function IssueStatsPage() {
  const { id, campaignId } = useParams<{ id: string; campaignId?: string }>()
  const backPath = campaignId ? `/campaigns/${campaignId}` : '/campaigns'
  const [showUnsub, setShowUnsub] = useState(false)

  const { data: issue } = useQuery({
    queryKey: ['issue', id],
    queryFn: async () => {
      const { data } = await supabase.from('issues').select('*').eq('id', id!).single()
      return data
    },
  })

  const { data: sends, isLoading } = useQuery({
    queryKey: ['sends', id],
    queryFn: async () => {
      const { data } = await supabase.from('sends').select('*, events(*)').eq('issue_id', id!)
      return data ?? []
    },
  })

  // Who unsubscribed from THIS issue — from the audit log, via this issue's sends.
  const { data: unsubPeople } = useQuery({
    queryKey: ['issue-unsubs', id],
    enabled: showUnsub && !!sends?.length,
    queryFn: async () => {
      const sendIds = (sends ?? []).map(s => s.id as string)
      if (!sendIds.length) return []
      const { data } = await supabase.from('suppression_events')
        .select('email, full_name, company, created_at')
        .in('send_id', sendIds).eq('action', 'unsubscribed')
        .order('created_at', { ascending: false })
      return (data ?? []) as { email: string; full_name: string | null; company: string | null; created_at: string }[]
    },
  })

  if (isLoading) return <div className="flex justify-center py-20"><Spinner /></div>

  const total = sends?.length ?? 0
  const has = (s: { events: { type: string }[] }, t: string) => s.events.some(e => e.type === t)
  const opens = sends?.filter(s => has(s, 'OPEN')).length ?? 0
  const clicks = sends?.filter(s => has(s, 'CLICK')).length ?? 0
  const bounces = sends?.filter(s => s.status === 'BOUNCED').length ?? 0
  const unsubs = sends?.filter(s => has(s, 'UNSUBSCRIBE')).length ?? 0

  const clickMap: Record<string, number> = {}
  sends?.forEach(s => {
    s.events.filter((e: { type: string }) => e.type === 'CLICK').forEach((e: { url: string | null }) => {
      if (e.url) clickMap[e.url] = (clickMap[e.url] ?? 0) + 1
    })
  })
  const topLinks = Object.entries(clickMap).sort((a, b) => b[1] - a[1]).slice(0, 10)

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Link to={backPath} className="btn-secondary p-2"><ArrowLeft className="w-4 h-4" /></Link>
        <div>
          <h1 className="text-xl font-bold text-gray-900">{issue?.subject}</h1>
          <p className="text-sm text-gray-500">Email stats</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <Stat label="Sent" value={total} sub="recipients" icon={Send} />
        <Stat label="Unique opens" value={opens} sub={`${rate(opens, total)}% of sent`} icon={Eye} accent="text-green-700" />
        <Stat label="Unique clicks" value={clicks} sub={`${rate(clicks, total)}% of sent`} icon={MousePointer} accent="text-blue-700" />
        <Stat label="Unsubscribes" value={unsubs} sub={`${rate(unsubs, total)}% of sent`} icon={UserX} accent="text-amber-700" onClick={() => setShowUnsub(true)} />
        <Stat label="Bounces" value={bounces} sub={`${rate(bounces, total)}% of sent`} icon={AlertTriangle} accent="text-red-600" />
      </div>

      {/* Visual rates */}
      <div className="card p-5 space-y-3">
        <h2 className="font-semibold text-gray-900 text-sm">Engagement</h2>
        <Bar label="Opened" n={opens} total={total} colour="bg-green-500" />
        <Bar label="Clicked" n={clicks} total={total} colour="bg-blue-500" />
        <Bar label="Unsubscribed" n={unsubs} total={total} colour="bg-amber-500" />
        <Bar label="Bounced" n={bounces} total={total} colour="bg-red-500" />
        <p className="text-[11px] text-gray-400">Opens are unique first-opens (scanners and instant prefetches are filtered), so the real number can be a little higher.</p>
      </div>

      {topLinks.length > 0 && (
        <div className="card">
          <div className="px-5 py-4 border-b border-gray-100">
            <h2 className="font-semibold text-gray-900">Click map</h2>
          </div>
          <div className="divide-y divide-gray-50">
            {topLinks.map(([url, count]) => (
              <div key={url} className="px-5 py-3 flex items-center justify-between">
                <a href={url} target="_blank" rel="noreferrer" className="text-sm text-brand-600 hover:underline truncate max-w-md">{url}</a>
                <div className="flex items-center gap-3 shrink-0">
                  <div className="w-24 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                    <div className="h-full rounded-full bg-brand-500" style={{ width: `${(count / (topLinks[0][1] || 1)) * 100}%` }} />
                  </div>
                  <span className="text-sm font-medium text-gray-700 w-6 text-right">{count}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Who unsubscribed */}
      {showUnsub && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setShowUnsub(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900">Unsubscribed from this email ({unsubs})</h3>
              <button onClick={() => setShowUnsub(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="overflow-y-auto">
              {!unsubPeople ? (
                <div className="flex justify-center py-10"><Spinner /></div>
              ) : !unsubPeople.length ? (
                <p className="text-sm text-gray-500 text-center py-10">No unsubscribes for this email.</p>
              ) : (
                <table className="w-full text-sm">
                  <tbody className="divide-y divide-gray-100">
                    {unsubPeople.map((p, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-5 py-2.5">
                          <p className="font-medium text-gray-900">{p.email}</p>
                          <p className="text-[11px] text-gray-400">{[p.full_name, p.company].filter(Boolean).join(' · ') || '—'}</p>
                        </td>
                        <td className="px-5 py-2.5 text-right text-[11px] text-gray-400 whitespace-nowrap">
                          {new Date(p.created_at).toLocaleString('en-GB', { timeZone: 'Europe/London', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
